import React, { useState } from "react";
import manejarUltimoReporte from "../api/bonos-flow";
import { HiOutlineMagnifyingGlass } from "react-icons/hi2";

const API_URL = "https://desarrollotecnologicoar.com/api5/tickets_resumen";

export default function TicketBonosSearch() {
  const [ticketQuery, setTicketQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [error, setError] = useState("");
  const [selectedReservaId, setSelectedReservaId] = useState(null);
  const [selectedTech, setSelectedTech] = useState(null);
  const [selectedTipoServicio, setSelectedTipoServicio] = useState("Instalacion y Capacitacion");

  async function buscar() {
    setError(""); setModalData(null); setResumen(null); setSelectedReservaId(null); setSelectedTech(null);
    const t = String(ticketQuery || "").trim();
    if (!t) { setError("Escribe un numero de ticket valido."); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/${encodeURIComponent(t)}`);
      if (!r.ok) { const msg = await r.text().catch(() => ""); throw new Error(msg || `HTTP ${r.status}`); }
      const data = await r.json();
      if (!data || !Array.isArray(data.reservas) || data.reservas.length === 0) throw new Error("No se encontraron reservas para ese ticket.");
      setResumen(data);
      setSelectedReservaId(data.reservas[0]?.id ?? null);
    } catch (e) { console.error(e); setError(e.message || "Error al consultar el ticket."); }
    finally { setLoading(false); }
  }

  const reservaSeleccionada = resumen?.reservas?.find((r) => r.id === selectedReservaId) || null;

  // Reportes que aún no han sido vinculados a un bono aprobado
  const reportesPendientesPorTecnico = resumen?.reportes?.reduce((acc, rep) => {
    if (rep.hoja_validada === true && rep.bonos_id != null) return acc;
    acc[rep.correo_tecnico] = (acc[rep.correo_tecnico] || 0) + 1;
    return acc;
  }, {}) ?? {};

  async function generarReporteBonos() {
    console.log("🚀 Generando reporte de bonos con:", { reservaSeleccionada, selectedTech, selectedTipoServicio });
    if (!resumen || !reservaSeleccionada || !selectedTech) { setError("Faltan datos: ticket, reserva o tecnico."); return; }
    try {
      setError(""); setModalData(null); setGenerating(true);
      const resp = await manejarUltimoReporte({
        reservaId: reservaSeleccionada.id,
        generarBono: true,
        selectedTipoServicio,
        reservaDetails: {
          ticket: reservaSeleccionada.ticket,
          empresa: reservaSeleccionada.empresa,
          localidad: reservaSeleccionada.localidad,
          tecnico: selectedTech.tecnico,
          correo_tecnico: selectedTech.correo_tecnico,
        },
      });
      console.log("Reporte de bonos generado:", resp);
      const hojasFallback = reportesPendientesPorTecnico[selectedTech.correo_tecnico] ?? 0;
      setModalData({ fueExtendido: resp.fueExtendido, noHojas: resp.noHojas ?? hojasFallback, bonoId: resp.bonoId });
      setTicketQuery("");
      setResumen(null);
      setSelectedReservaId(null);
      setSelectedTech(null);
      setSelectedTipoServicio("Instalacion y Capacitacion");
    } catch (e) { console.error(e); setError("No se pudo finalizar y registrar el bono. Reintenta."); }
    finally { setGenerating(false); }
  }

  return (
    <div className="page-container">
      <h1 className="page-title mb-6">Buscar Ticket para Bonos</h1>

      {/* Search bar */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <HiOutlineMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Ej. 27467" value={ticketQuery} onChange={(e) => setTicketQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && buscar()} className="input pl-9 py-2 text-sm" />
        </div>
        <button onClick={buscar} disabled={loading || generating} className="btn-primary text-sm">
          {loading ? <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg> : "Buscar"}
        </button>
      </div>

      {/* Alerts */}
      {error && <div className="p-3 mb-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}

      {/* Modal de confirmación */}
      {modalData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setModalData(null)}>
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-5">
              <span className="text-2xl">✅</span>
              <h2 className="text-lg font-semibold text-slate-900">Reporte de bono generado</h2>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Status</span>
                {modalData.fueExtendido ? (
                  <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold text-xs">Pendiente</span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold text-xs">Aprobado</span>
                )}
              </div>
              {modalData.fueExtendido && (
                <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs">
                  El itinerario fue extendido. El bono quedó pendiente de revisión manual.
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Hojas registradas</span>
                <span className="font-semibold text-slate-900">{modalData.noHojas ?? "—"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">ID de bono</span>
                <span className="font-semibold text-slate-900">#{modalData.bonoId}</span>
              </div>
            </div>
            <button onClick={() => setModalData(null)} className="mt-5 w-full btn-primary text-sm">
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* Results card */}
      {resumen && reservaSeleccionada && (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="section-title">Resumen del Ticket</h2>
            <span className="badge-blue">Ticket: {reservaSeleccionada.ticket}</span>
          </div>

          <div className="space-y-4">
            {resumen.reservas.length > 1 && (
              <div>
                <label className="label">Reserva</label>
                <select value={selectedReservaId ?? ""} onChange={(e) => setSelectedReservaId(Number(e.target.value))} className="input max-w-xs">
                  {resumen.reservas.map((r) => <option key={r.id} value={r.id}>#{r.id} -- {r.empresa} ({r.localidad})</option>)}
                </select>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <span className="badge-gray">Empresa: {reservaSeleccionada.empresa}</span>
              <span className="badge-gray">Localidad: {reservaSeleccionada.localidad}</span>
            </div>

            <hr className="border-slate-200" />

            <div>
              <label className="label">Tipo de servicio</label>
              <select value={selectedTipoServicio} onChange={(e) => setSelectedTipoServicio(e.target.value)} className="input max-w-xs">
                <option value="Instalacion y Capacitacion">Instalacion y Capacitacion</option>
                <option value="Servicio en Garantia">Servicio en Garantia</option>
                <option value="Servicio Pagado">Servicio Pagado</option>
                <option value="Expo">Expo</option>
              </select>
            </div>

            <div>
              <p className="font-semibold text-slate-900 mb-2">Tecnicos con reportes en este ticket</p>
              {Array.isArray(resumen.tecnicos) && resumen.tecnicos.length > 0 ? (
                <div className="space-y-2">
                  {resumen.tecnicos.map((t) => {
                    const pendientes = reportesPendientesPorTecnico[t.correo_tecnico] ?? 0;
                    const sinPendientes = pendientes === 0;
                    return (
                      <label key={t.correo_tecnico} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${sinPendientes ? "border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed" : `cursor-pointer ${selectedTech?.correo_tecnico === t.correo_tecnico ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}`}>
                        <input type="radio" name="tecnico" value={t.correo_tecnico} checked={selectedTech?.correo_tecnico === t.correo_tecnico} onChange={() => !sinPendientes && setSelectedTech(t)} disabled={sinPendientes} className="accent-slate-900" />
                        <span className="text-sm text-slate-800">{t.tecnico}</span>
                        {sinPendientes ? (
                          <span className="badge-gray text-xs">Sin reportes pendientes</span>
                        ) : (
                          <span className="badge-gray text-xs">{pendientes} reporte{pendientes !== 1 ? "s" : ""} pendiente{pendientes !== 1 ? "s" : ""}</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-700">Este ticket no tiene tecnicos con reportes.</div>
              )}
            </div>

            <button onClick={generarReporteBonos} disabled={!selectedTech || generating || loading || (reportesPendientesPorTecnico[selectedTech?.correo_tecnico] ?? 0) === 0} className="text-sm py-2 px-4 inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-btn font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50">
              {generating ? <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg> : "Generar reporte de bonos"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
